import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { api_urls } from "../../utilities";
import {
  deleteService,
  getService,
  patchService,
  postService,
} from "../services";

function normalizeServiceList(inner) {
  if (inner == null) return [];
  if (Array.isArray(inner)) return inner;
  if (Array.isArray(inner.data)) return inner.data;
  return (
    inner.services ?? inner.records ?? inner.list ?? []
  );
}

export const fetchServices = createAsyncThunk(
  "service/fetchServices",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getService(api_urls.services, {});
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && code !== 200) {
        return rejectWithValue({
          httpStatus: response.status,
          body: envelope,
        });
      }
      const inner = envelope.data ?? envelope;
      return normalizeServiceList(inner);
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

export const createService = createAsyncThunk(
  "service/createService",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postService(api_urls.services, payload);
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && code !== 200) {
        return rejectWithValue({
          httpStatus: response.status,
          body: envelope,
        });
      }
      return envelope.data ?? envelope;
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

export const updateService = createAsyncThunk(
  "service/updateService",
  async ({ id, ...payload }, { rejectWithValue }) => {
    try {
      const response = await patchService(api_urls.serviceById(id), payload);
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && code !== 200) {
        return rejectWithValue({
          httpStatus: response.status,
          body: envelope,
        });
      }
      return {
        id,
        data: envelope.data ?? envelope,
      };
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

export const deleteServiceById = createAsyncThunk(
  "service/deleteServiceById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteService(api_urls.serviceById(id));
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && code !== 200) {
        return rejectWithValue({
          httpStatus: response.status,
          body: envelope,
        });
      }
      return { id, data: envelope.data ?? envelope };
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

const initialState = {
  list: [],
  loading: false,
  saving: false,
  deleting: false,
};

export const serviceSlice = createSlice({
  name: "service",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchServices.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchServices.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.list = Array.isArray(payload) ? payload : [];
      })
      .addCase(fetchServices.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createService.pending, (state) => {
        state.saving = true;
      })
      .addCase(createService.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(createService.rejected, (state) => {
        state.saving = false;
      })
      .addCase(updateService.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateService.fulfilled, (state, { payload }) => {
        state.saving = false;
        if (payload?.id != null && payload?.data) {
          const merged = payload.data;
          state.list = state.list.map((s) =>
            String(s.id) === String(payload.id) ? { ...s, ...merged } : s
          );
        }
      })
      .addCase(updateService.rejected, (state) => {
        state.saving = false;
      })
      .addCase(deleteServiceById.pending, (state) => {
        state.deleting = true;
      })
      .addCase(deleteServiceById.fulfilled, (state, { payload }) => {
        state.deleting = false;
        if (payload?.id != null) {
          state.list = state.list.filter(
            (s) => String(s.id) !== String(payload.id)
          );
        }
      })
      .addCase(deleteServiceById.rejected, (state) => {
        state.deleting = false;
      });
  },
});

export default serviceSlice.reducer;
